import { WaitForQueries } from '@graphcommerce/ecommerce-ui'
import { PageOptions } from '@graphcommerce/framer-next-pages'
import { useCustomerSession } from '@graphcommerce/magento-customer'
import { Money, PageMeta, StoreConfigDocument } from '@graphcommerce/magento-store'
import {
  useWishlistItems,
  WishlistItemActionCard,
  WishlistItemActionCardProps,
} from '@graphcommerce/magento-wishlist'
import {
  GetStaticProps,
  iconHeart,
  FullPageMessage,
  Button,
  LayoutTitle,
  IconSvg,
  LayoutOverlayHeader,
} from '@graphcommerce/next-ui'
import { i18n } from '@lingui/core'
import { Trans } from '@lingui/react'
import { CircularProgress, Container, Theme, useMediaQuery } from '@mui/material'
import { LayoutOverlay, LayoutOverlayProps, ProductSignInButton } from '../../components'
import { graphqlSharedClient } from '../../lib/graphql/graphqlSsrClient'
import { useStoreData } from '../../lib/hooks/useStoreData'

type Props = Record<string, unknown>
type GetPageStaticProps = GetStaticProps<LayoutOverlayProps, Props>

function WishlistPage() {
  const wishlistItems = useWishlistItems()

  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down('md'), {
    defaultMatches: false,
  })

  const { loggedIn } = useCustomerSession()
  const { mode } = useStoreData()
  const storeB2B = mode === 'b2b'
  const loggedOutB2B = !loggedIn && storeB2B

  const size: WishlistItemActionCardProps['size'] = isMobile ? 'small' : 'large'

  return (
    <>
      <PageMeta title={i18n._(/* i18n */ 'Wishlist')} metaRobots={['noindex']} />
      <LayoutOverlayHeader
        switchPoint={0}
        noAlign
        sx={(theme) => ({
          '&.noAlign': { marginBottom: theme.spacings.sm },
          '& + .MuiContainer-root': { marginBottom: theme.spacings.sm },
        })}
      >
        <LayoutTitle component='span' size='small' icon={iconHeart}>
          <Trans id='Wishlist' />
        </LayoutTitle>
      </LayoutOverlayHeader>

      <WaitForQueries
        waitFor={[wishlistItems]}
        fallback={
          <FullPageMessage title={<Trans id='Loading' />} icon={<CircularProgress />}>
            <Trans id='We are fetching your favorite products, one moment please!' />
          </FullPageMessage>
        }
      >
        <Container maxWidth='md'>
          {wishlistItems.items.length === 0 ? (
            <FullPageMessage
              title={<Trans id='Your wishlist is empty' />}
              icon={<IconSvg src={iconHeart} size='xxl' />}
              button={
                <Button href='/' variant='pill' color='primary' size='large'>
                  <Trans id='Continue shopping' />
                </Button>
              }
            >
              <Trans id='Discover our collection and add items to your wishlist!' />
            </FullPageMessage>
          ) : (
            <>
              {wishlistItems.items.map((item) => {
                const incompleteConfiguration =
                  item?.__typename === 'ConfigurableWishlistItem' &&
                  item?.product?.__typename === 'ConfigurableProduct'
                    ? item?.product?.configurable_options?.length !==
                      item?.configurable_options?.length
                    : false

                return (
                  <WishlistItemActionCard
                    price={
                      loggedOutB2B ? (
                        <ProductSignInButton />
                      ) : (
                        <Money {...item.product?.price_range.minimum_price.final_price} />
                      )
                    }
                    key={item.id}
                    item={item}
                    size={size}
                    sx={(theme) => ({
                      '& .ActionCard-secondaryAction > .MuiBox-root': {
                        alignItems: 'center',
                        gap: theme.spacings.xxs,
                        [theme.breakpoints.down('sm')]: {
                          flexDirection: 'column',
                        },
                      },
                      '& .ActionCard-secondaryAction': {
                        display:
                          (!loggedOutB2B && item.product?.stock_status === 'IN_STOCK') ||
                          incompleteConfiguration
                            ? 'flex'
                            : 'none',
                      },
                      '& .ActionCard-details': {
                        flex: 1,
                      },
                    })}
                  />
                )
              })}
            </>
          )}
        </Container>
      </WaitForQueries>
    </>
  )
}

const pageOptions: PageOptions<LayoutOverlayProps> = {
  overlayGroup: 'bottom',
  Layout: LayoutOverlay,
  layoutProps: { variantMd: 'bottom', variantSm: 'bottom' },
}
WishlistPage.pageOptions = pageOptions

export default WishlistPage

export const getStaticProps: GetPageStaticProps = async ({ locale }) => {
  const client = graphqlSharedClient(locale)
  const conf = client.query({ query: StoreConfigDocument })

  return {
    props: {
      apolloState: await conf.then(() => client.cache.extract()),
    },
  }
}
